import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Settings, 
  Palette, 
  Type, 
  Save,
  RefreshCw,
  Eye,
  Shield,
  Upload,
  Trash2,
  Image,
  Bell,
  BellOff,
  UserCog,
  RotateCcw,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { 
  isPushSupported, 
  registerServiceWorker, 
  subscribeToPush, 
  requestNotificationPermission 
} from '../services/pushNotifications';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FONT_OPTIONS = [
  { value: 'Chivo', label: 'Chivo (Default)', category: 'heading' },
  { value: 'Inter', label: 'Inter (Default Body)', category: 'body' },
  { value: 'JetBrains Mono', label: 'JetBrains Mono', category: 'mono' },
  { value: 'Roboto', label: 'Roboto', category: 'body' },
  { value: 'Open Sans', label: 'Open Sans', category: 'body' },
  { value: 'Lato', label: 'Lato', category: 'body' },
  { value: 'Montserrat', label: 'Montserrat', category: 'heading' },
  { value: 'Poppins', label: 'Poppins', category: 'body' },
  { value: 'Oswald', label: 'Oswald', category: 'heading' },
  { value: 'Raleway', label: 'Raleway', category: 'body' },
  { value: 'Playfair Display', label: 'Playfair Display', category: 'heading' },
  { value: 'Source Sans Pro', label: 'Source Sans Pro', category: 'body' },
];

const COLOR_PRESETS = [
  { name: 'Blue (Default)', value: '#3b82f6' },
  { name: 'Red', value: '#ef4444' },
  { name: 'Green', value: '#22c55e' },
  { name: 'Purple', value: '#a855f7' },
  { name: 'Orange', value: '#f97316' },
  { name: 'Cyan', value: '#06b6d4' },
  { name: 'Pink', value: '#ec4899' },
  { name: 'Yellow', value: '#eab308' },
];

const SettingsPage = () => {
  const { isSecurityHead, user, token, pushEnabled, hasPermission } = useAuth();
  const { refreshSettings, getLogoUrl } = useSettings();
  const [settings, setSettings] = useState({
    app_title: 'Truth Church',
    app_subtitle: 'Security System',
    primary_color: '#3b82f6',
    font_heading: 'Chivo',
    font_body: 'Inter',
    logo_url: '',
    custom_css: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [pushStatus, setPushStatus] = useState({ supported: false, enabled: false, permission: 'default' });
  const [roleTestStatus, setRoleTestStatus] = useState({ is_testing: false, current_role: '', original_role: '', available_roles: [] });
  const [switchingRole, setSwitchingRole] = useState(false);
  const logoInputRef = useRef(null);

  useEffect(() => {
    fetchSettings();
    checkPushStatus();
    if (isSecurityHead || user?.original_role === 'security_head') {
      fetchRoleTestStatus();
    }
  }, [isSecurityHead, user]);

  const checkPushStatus = () => {
    const supported = isPushSupported();
    const permission = 'Notification' in window ? Notification.permission : 'denied';
    setPushStatus({
      supported,
      enabled: pushEnabled,
      permission
    });
  };

  const enablePushNotifications = async () => {
    try {
      if (!isPushSupported()) {
        toast.error('Push notifications not supported on this device');
        return;
      }

      await registerServiceWorker();
      const permission = await requestNotificationPermission();
      
      if (permission === 'granted') {
        const result = await subscribeToPush(token);
        if (result.success) {
          toast.success('Push notifications enabled! You will receive alerts even when the app is closed.');
          setPushStatus(prev => ({ ...prev, enabled: true, permission: 'granted' }));
        } else {
          toast.error('Failed to enable push notifications');
        }
      } else if (permission === 'denied') {
        toast.error('Notification permission denied. Please enable in browser settings.');
      }
    } catch (error) {
      console.error('Push notification error:', error);
      toast.error('Failed to enable notifications');
    }
  };

  const fetchRoleTestStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/test-role/status`);
      setRoleTestStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch role test status:', error);
    }
  };

  const switchToRole = async (role) => {
    setSwitchingRole(true);
    try {
      const response = await axios.post(`${API_URL}/test-role/switch`, { role });
      toast.success(`Switched to ${role.replace('_', ' ')} role for testing`);
      setRoleTestStatus(prev => ({
        ...prev,
        is_testing: true,
        current_role: role,
        original_role: response.data.original_role
      }));
      // Reload page to apply new permissions
      setTimeout(() => window.location.reload(), 500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to switch role');
    } finally {
      setSwitchingRole(false);
    }
  };

  const resetRole = async () => {
    setSwitchingRole(true);
    try {
      const response = await axios.post(`${API_URL}/test-role/reset`);
      toast.success(`Reset to ${response.data.role.replace('_', ' ')} role`);
      setRoleTestStatus(prev => ({
        ...prev,
        is_testing: false,
        current_role: response.data.role,
        original_role: null
      }));
      // Reload page to apply original permissions
      setTimeout(() => window.location.reload(), 500);
    } catch (error) {
      toast.error('Failed to reset role');
    } finally {
      setSwitchingRole(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/settings`);
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/settings`, settings);
      toast.success('Settings saved! Refresh to see changes.');
      
      // Apply changes immediately
      applySettings(settings);
      
      // Refresh global settings context
      refreshSettings();
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    const maxSize = 2 * 1024 * 1024; // 2MB
    if (file.size > maxSize) {
      toast.error('Logo too large. Max 2MB allowed.');
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Use JPEG, PNG, GIF, WebP, or SVG.');
      return;
    }

    setUploadingLogo(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/settings/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSettings(prev => ({ ...prev, logo_url: response.data.url }));
      toast.success('Logo uploaded!');
      refreshSettings();
      // Reset file input
      if (logoInputRef.current) {
        logoInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Logo upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleLogoDelete = async () => {
    try {
      await axios.delete(`${API_URL}/settings/logo`);
      setSettings(prev => ({ ...prev, logo_url: '' }));
      toast.success('Logo removed');
      refreshSettings();
    } catch (error) {
      toast.error('Failed to remove logo');
    }
  };

  const applySettings = (s) => {
    // Update CSS variables
    const root = document.documentElement;
    
    // Convert hex to HSL for CSS variables
    if (s.primary_color) {
      root.style.setProperty('--primary-custom', s.primary_color);
    }
    
    // Update fonts
    if (s.font_heading) {
      root.style.setProperty('--font-heading', s.font_heading);
    }
    if (s.font_body) {
      root.style.setProperty('--font-body', s.font_body);
    }
  };

  const resetToDefaults = () => {
    setSettings({
      app_title: 'Truth Church',
      app_subtitle: 'Security System',
      primary_color: '#3b82f6',
      font_heading: 'Chivo',
      font_body: 'Inter',
      logo_url: '',
      custom_css: ''
    });
    toast.info('Settings reset to defaults (not saved)');
  };

  const canEditSettings = hasPermission('can_edit_settings');
  const canAccessRoleSwitcher = isSecurityHead || roleTestStatus.original_role === 'security_head';

  // Allow access if user can edit settings OR if they're an admin testing another role
  if (!canEditSettings && !canAccessRoleSwitcher) {
    return (
      <div className="p-4 md:p-6 flex items-center justify-center min-h-[60vh]">
        <Card className="bg-panel border-white/10 rounded-sm max-w-md w-full">
          <CardContent className="p-8 text-center">
            <Shield className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="font-chivo font-bold text-xl text-white mb-2">Access Restricted</h2>
            <p className="font-inter text-sm text-muted-foreground">
              You don't have permission to modify app settings.
            </p>
            {roleTestStatus.is_testing && (
              <Button
                onClick={resetRole}
                className="mt-4 bg-yellow-500 hover:bg-yellow-600 text-black"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Exit Test Mode
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="settings-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-chivo font-black text-2xl md:text-3xl tracking-tight uppercase text-white">
            App Settings
          </h1>
          <p className="font-mono text-xs tracking-widest uppercase text-muted-foreground mt-1">
            Customize appearance & branding
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={resetToDefaults}
            className="border-white/20 hover:bg-white/5 rounded-sm font-mono text-xs uppercase"
            data-testid="reset-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Reset
          </Button>
          <Button
            onClick={saveSettings}
            disabled={saving}
            className="bg-green-600 hover:bg-green-700 rounded-sm font-bold uppercase"
            data-testid="save-settings-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Role Testing Banner - Show if currently testing */}
      {roleTestStatus.is_testing && (
        <Card className="bg-yellow-900/30 border-yellow-500/50 rounded-sm">
          <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0" />
              <div>
                <p className="text-sm font-bold text-yellow-500">TEST MODE ACTIVE</p>
                <p className="text-xs text-yellow-500/80">
                  You are viewing the app as: <span className="font-mono font-bold">{roleTestStatus.current_role?.replace(/_/g, ' ')}</span>
                </p>
              </div>
            </div>
            <Button
              onClick={resetRole}
              disabled={switchingRole}
              size="sm"
              className="bg-yellow-500 hover:bg-yellow-600 text-black rounded-sm font-bold"
              data-testid="reset-role-btn"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              {switchingRole ? 'Resetting...' : 'Exit Test Mode'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Role Switcher Card - For Admins Only */}
      {(isSecurityHead || roleTestStatus.original_role === 'security_head') && (
        <Card className="bg-panel border-white/10 rounded-sm" data-testid="role-switcher-card">
          <CardHeader className="border-b border-white/10 pb-4">
            <CardTitle className="font-chivo font-bold text-lg text-white flex items-center gap-2">
              <UserCog className="w-5 h-5 text-primary" />
              Role Testing Mode
            </CardTitle>
            <CardDescription className="text-muted-foreground">
              Temporarily view the app as different roles to test permissions. Your original role will be preserved.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4 space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
              {roleTestStatus.available_roles?.map((role) => {
                const isCurrentRole = roleTestStatus.current_role === role;
                const isOriginalRole = !roleTestStatus.is_testing && user?.role === role;
                return (
                  <Button
                    key={role}
                    variant={isCurrentRole ? "default" : "outline"}
                    size="sm"
                    onClick={() => switchToRole(role)}
                    disabled={switchingRole || isCurrentRole}
                    className={`font-mono text-[10px] uppercase rounded-sm truncate ${
                      isCurrentRole 
                        ? 'bg-primary text-primary-foreground' 
                        : isOriginalRole
                        ? 'border-green-500/50 text-green-400'
                        : 'border-white/20'
                    }`}
                    data-testid={`role-btn-${role}`}
                  >
                    {role.replace(/_/g, ' ')}
                    {isOriginalRole && ' ★'}
                  </Button>
                );
              })}
            </div>
            <p className="text-[10px] text-muted-foreground font-mono">
              ★ = Your actual role • Click any role to test its permissions • Page will reload after switching
            </p>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Settings Panel */}
        <div className="lg:col-span-2 space-y-6">
          <Tabs defaultValue="branding">
            <TabsList className="bg-panel border border-white/10 rounded-sm p-1 flex-wrap">
              <TabsTrigger 
                value="branding" 
                className="font-mono text-xs uppercase rounded-sm data-[state=active]:bg-white data-[state=active]:text-black"
              >
                <Type className="w-4 h-4 mr-2" />
                Branding
              </TabsTrigger>
              <TabsTrigger 
                value="colors" 
                className="font-mono text-xs uppercase rounded-sm data-[state=active]:bg-white data-[state=active]:text-black"
              >
                <Palette className="w-4 h-4 mr-2" />
                Colors
              </TabsTrigger>
              <TabsTrigger 
                value="advanced" 
                className="font-mono text-xs uppercase rounded-sm data-[state=active]:bg-white data-[state=active]:text-black"
              >
                <Settings className="w-4 h-4 mr-2" />
                Advanced
              </TabsTrigger>
            </TabsList>

            <TabsContent value="branding" className="mt-4 space-y-4">
              <Card className="bg-panel border-white/10 rounded-sm">
                <CardHeader>
                  <CardTitle className="font-chivo font-bold text-lg text-white">Logo</CardTitle>
                  <CardDescription className="font-inter text-muted-foreground">
                    Upload a custom logo for your organization (max 2MB)
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Logo Preview */}
                  <div className="flex items-center gap-4">
                    <div className="w-20 h-20 rounded-sm bg-black border border-white/20 flex items-center justify-center overflow-hidden">
                      {settings.logo_url ? (
                        <img 
                          src={settings.logo_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${settings.logo_url}` : settings.logo_url}
                          alt="Logo"
                          className="w-full h-full object-contain p-2"
                        />
                      ) : (
                        <Image className="w-8 h-8 text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex flex-col gap-2">
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
                        onChange={handleLogoUpload}
                        className="hidden"
                      />
                      <Button
                        variant="outline"
                        onClick={() => logoInputRef.current?.click()}
                        disabled={uploadingLogo}
                        className="border-white/20 rounded-sm"
                        data-testid="upload-logo-btn"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                      </Button>
                      {settings.logo_url && (
                        <Button
                          variant="outline"
                          onClick={handleLogoDelete}
                          className="border-red-600/50 text-red-400 hover:bg-red-600/20 rounded-sm"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Remove Logo
                        </Button>
                      )}
                    </div>
                  </div>
                  <p className="font-mono text-[10px] text-muted-foreground">
                    Supported: JPEG, PNG, GIF, WebP, SVG • Max 2MB
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-panel border-white/10 rounded-sm">
                <CardHeader>
                  <CardTitle className="font-chivo font-bold text-lg text-white">App Title & Subtitle</CardTitle>
                  <CardDescription className="font-inter text-muted-foreground">
                    Customize the main branding text shown in the header and login page
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase text-muted-foreground">App Title</Label>
                    <Input
                      value={settings.app_title}
                      onChange={(e) => setSettings({ ...settings, app_title: e.target.value })}
                      placeholder="Truth Church"
                      className="bg-black border-white/20 rounded-sm"
                      data-testid="app-title-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase text-muted-foreground">Subtitle</Label>
                    <Input
                      value={settings.app_subtitle}
                      onChange={(e) => setSettings({ ...settings, app_subtitle: e.target.value })}
                      placeholder="Security System"
                      className="bg-black border-white/20 rounded-sm"
                      data-testid="app-subtitle-input"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-panel border-white/10 rounded-sm">
                <CardHeader>
                  <CardTitle className="font-chivo font-bold text-lg text-white">Typography</CardTitle>
                  <CardDescription className="font-inter text-muted-foreground">
                    Choose fonts for headings and body text
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase text-muted-foreground">Heading Font</Label>
                    <Select 
                      value={settings.font_heading} 
                      onValueChange={(v) => setSettings({ ...settings, font_heading: v })}
                    >
                      <SelectTrigger className="bg-black border-white/20 rounded-sm" data-testid="font-heading-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-panel border-white/10 rounded-sm">
                        {FONT_OPTIONS.filter(f => f.category === 'heading' || f.category === 'body').map((font) => (
                          <SelectItem 
                            key={font.value} 
                            value={font.value} 
                            className="font-mono text-sm"
                            style={{ fontFamily: font.value }}
                          >
                            {font.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase text-muted-foreground">Body Font</Label>
                    <Select 
                      value={settings.font_body} 
                      onValueChange={(v) => setSettings({ ...settings, font_body: v })}
                    >
                      <SelectTrigger className="bg-black border-white/20 rounded-sm" data-testid="font-body-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-panel border-white/10 rounded-sm">
                        {FONT_OPTIONS.filter(f => f.category === 'body').map((font) => (
                          <SelectItem 
                            key={font.value} 
                            value={font.value} 
                            className="font-mono text-sm"
                            style={{ fontFamily: font.value }}
                          >
                            {font.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="colors" className="mt-4">
              <Card className="bg-panel border-white/10 rounded-sm">
                <CardHeader>
                  <CardTitle className="font-chivo font-bold text-lg text-white">Primary Color</CardTitle>
                  <CardDescription className="font-inter text-muted-foreground">
                    Choose the main accent color for buttons and highlights
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase text-muted-foreground">Color Presets</Label>
                    <div className="grid grid-cols-4 gap-2">
                      {COLOR_PRESETS.map((color) => (
                        <button
                          key={color.value}
                          onClick={() => setSettings({ ...settings, primary_color: color.value })}
                          className={`h-12 rounded-sm border-2 transition-all ${
                            settings.primary_color === color.value 
                              ? 'border-white scale-105' 
                              : 'border-transparent hover:border-white/30'
                          }`}
                          style={{ backgroundColor: color.value }}
                          title={color.name}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase text-muted-foreground">Custom Color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={settings.primary_color}
                        onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                        className="w-16 h-10 p-1 bg-black border-white/20 rounded-sm cursor-pointer"
                      />
                      <Input
                        value={settings.primary_color}
                        onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                        placeholder="#3b82f6"
                        className="bg-black border-white/20 rounded-sm font-mono"
                        data-testid="primary-color-input"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="advanced" className="mt-4">
              <Card className="bg-panel border-white/10 rounded-sm">
                <CardHeader>
                  <CardTitle className="font-chivo font-bold text-lg text-white">Custom CSS</CardTitle>
                  <CardDescription className="font-inter text-muted-foreground">
                    Add custom CSS styles for advanced customization
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Textarea
                    value={settings.custom_css}
                    onChange={(e) => setSettings({ ...settings, custom_css: e.target.value })}
                    placeholder="/* Add custom CSS here */
.custom-class {
  color: red;
}"
                    className="bg-black border-white/20 rounded-sm font-mono text-sm min-h-[200px]"
                    data-testid="custom-css-input"
                  />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Preview Panel */}
        <div className="space-y-4">
          <Card className="bg-panel border-white/10 rounded-sm">
            <CardHeader className="border-b border-white/10 pb-4">
              <CardTitle className="font-chivo font-bold text-lg text-white flex items-center gap-2">
                <Eye className="w-5 h-5" />
                Live Preview
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              {/* Mini Preview */}
              <div className="bg-void rounded-sm p-4 border border-white/10">
                {/* Header Preview */}
                <div className="flex items-center gap-2 mb-4 pb-4 border-b border-white/10">
                  <div 
                    className="w-8 h-8 rounded-sm flex items-center justify-center overflow-hidden"
                    style={{ backgroundColor: `${settings.primary_color}30` }}
                  >
                    {settings.logo_url ? (
                      <img 
                        src={settings.logo_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${settings.logo_url}` : settings.logo_url}
                        alt="Logo"
                        className="w-full h-full object-contain p-0.5"
                      />
                    ) : (
                      <Shield className="w-4 h-4" style={{ color: settings.primary_color }} />
                    )}
                  </div>
                  <div>
                    <p 
                      className="font-bold text-xs uppercase text-white"
                      style={{ fontFamily: settings.font_heading }}
                    >
                      {settings.app_title}
                    </p>
                    <p className="font-mono text-[8px] text-muted-foreground uppercase">
                      {settings.app_subtitle}
                    </p>
                  </div>
                </div>

                {/* Content Preview */}
                <div className="space-y-3">
                  <h3 
                    className="font-bold text-sm text-white"
                    style={{ fontFamily: settings.font_heading }}
                  >
                    Sample Heading
                  </h3>
                  <p 
                    className="text-xs text-muted-foreground"
                    style={{ fontFamily: settings.font_body }}
                  >
                    This is sample body text showing your selected fonts.
                  </p>
                  <button
                    className="px-3 py-1.5 rounded-sm text-white text-xs font-bold uppercase"
                    style={{ backgroundColor: settings.primary_color }}
                  >
                    Button
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Push Notification Settings */}
          <Card className="bg-panel border-white/10 rounded-sm">
            <CardHeader className="border-b border-white/10 pb-4">
              <CardTitle className="font-chivo font-bold text-lg text-white flex items-center gap-2">
                {pushStatus.enabled ? (
                  <Bell className="w-5 h-5 text-green-500" />
                ) : (
                  <BellOff className="w-5 h-5 text-muted-foreground" />
                )}
                Alert Notifications
              </CardTitle>
              <CardDescription className="text-muted-foreground">
                Receive emergency alerts even when app is closed
              </CardDescription>
            </CardHeader>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Status:</span>
                <span className={`text-sm font-mono ${pushStatus.enabled ? 'text-green-500' : 'text-yellow-500'}`}>
                  {pushStatus.enabled ? '✓ Enabled' : '○ Disabled'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Permission:</span>
                <span className={`text-sm font-mono ${pushStatus.permission === 'granted' ? 'text-green-500' : 'text-yellow-500'}`}>
                  {pushStatus.permission}
                </span>
              </div>
              {!pushStatus.enabled && (
                <Button 
                  onClick={enablePushNotifications}
                  className="w-full bg-primary hover:bg-primary/80 rounded-sm"
                >
                  <Bell className="w-4 h-4 mr-2" />
                  Enable Push Notifications
                </Button>
              )}
              {pushStatus.enabled && (
                <p className="text-xs text-green-500/70 text-center">
                  ✓ You will receive alerts on this device
                </p>
              )}
            </CardContent>
          </Card>

          <Card className="bg-panel border-white/10 rounded-sm">
            <CardHeader>
              <CardTitle className="font-mono text-xs uppercase text-muted-foreground">
                Current Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-xs">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Title:</span>
                <span className="text-white font-mono">{settings.app_title}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Subtitle:</span>
                <span className="text-white font-mono">{settings.app_subtitle}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Primary:</span>
                <div className="flex items-center gap-2">
                  <div 
                    className="w-4 h-4 rounded-sm"
                    style={{ backgroundColor: settings.primary_color }}
                  />
                  <span className="text-white font-mono">{settings.primary_color}</span>
                </div>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Heading:</span>
                <span className="text-white font-mono">{settings.font_heading}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Body:</span>
                <span className="text-white font-mono">{settings.font_body}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
